/**
 * A tiny module-level store for global pointer + scroll state.
 *
 * Every 3D canvas on the page sits behind the UI with `pointer-events: none`,
 * so none of them receive pointer events of their own. Instead they all read
 * from here, which keeps the parallax perfectly in sync across canvases and
 * costs exactly one set of window listeners for the whole app.
 */

export const viewer = {
  /** pointer, normalised to -1..1 with the origin at the centre of the viewport */
  px: 0,
  py: 0,
  /** where the pointer actually is, in client pixels */
  clientX: 0,
  clientY: 0,
  /** raw scroll offset, plus how far it moved since the last frame read it */
  scrollY: 0,
  scrollDelta: 0,
  /** 0 at the top of the document, 1 at the bottom */
  progress: 0,
};

let lastScrollY = 0;
let started = false;

function handlePointer(x, y) {
  viewer.clientX = x;
  viewer.clientY = y;
  viewer.px = (x / window.innerWidth) * 2 - 1;
  viewer.py = -((y / window.innerHeight) * 2 - 1);
}

function handleScroll() {
  const y = window.scrollY || window.pageYOffset || 0;
  viewer.scrollDelta += y - lastScrollY;
  lastScrollY = y;
  viewer.scrollY = y;
  const scrollable = document.body.scrollHeight - window.innerHeight;
  viewer.progress = scrollable > 0 ? Math.min(1, Math.max(0, y / scrollable)) : 0;
}

/** Idempotent — safe to call from every component that needs the store. */
export function startViewerTracking() {
  if (started || typeof window === 'undefined') return;
  started = true;

  lastScrollY = window.scrollY || 0;
  viewer.scrollY = lastScrollY;

  window.addEventListener(
    'pointermove',
    (e) => handlePointer(e.clientX, e.clientY),
    { passive: true }
  );
  window.addEventListener(
    'touchmove',
    (e) => {
      if (e.touches[0]) handlePointer(e.touches[0].clientX, e.touches[0].clientY);
    },
    { passive: true }
  );
  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleScroll, { passive: true });
  handleScroll();
}

/**
 * Reads and clears the accumulated scroll delta. Only one consumer per frame
 * should call this — the ambient scene owns it, since it is the only place
 * scroll momentum is turned into motion.
 */
export function consumeScrollDelta() {
  const d = viewer.scrollDelta;
  viewer.scrollDelta = 0;
  return d;
}

/** Frame-rate independent lerp factor for a given smoothing half-life. */
export function damp(current, target, lambda, delta) {
  return current + (target - current) * (1 - Math.exp(-lambda * delta));
}
